import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, List, Avatar, Tag, Button, message, Modal } from 'antd';
import {
  UserOutlined,
  MessageOutlined,
  RobotOutlined,
  DatabaseOutlined,
  RiseOutlined,
  FallOutlined,
  ReloadOutlined,
  EyeOutlined,
  SettingOutlined,
  PlusOutlined,
  BookOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statsData, setStatsData] = useState({
    totalUsers: 12580,
    todayConversations: 3240,
    activeAgents: 8,
    knowledgeItems: 1560,
  });
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState('');
  const [conversationModalVisible, setConversationModalVisible] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<any>(null);

  // 刷新数据
  const handleRefresh = () => {
    setLoading(true);
    message.loading('正在刷新数据...', 1);
    setTimeout(() => {
      setStatsData(prev => ({
        totalUsers: prev.totalUsers + Math.floor(Math.random() * 100),
        todayConversations: prev.todayConversations + Math.floor(Math.random() * 50),
        activeAgents: prev.activeAgents,
        knowledgeItems: prev.knowledgeItems + Math.floor(Math.random() * 20),
      }));
      setRefreshKey(prev => prev + 1);
      setLoading(false);
      message.success('数据刷新成功！');
    }, 1500);
  };

  // 查看详情
  const handleViewDetail = (type: string) => {
    setSelectedDetail(type);
    setDetailModalVisible(true);
    message.info(`正在加载${type}详情...`);
  };

  // 查看对话详情
  const handleViewConversation = (conversation: any) => {
    setSelectedConversation(conversation);
    setConversationModalVisible(true);
  };

  // 系统操作
  const handleSystemAction = (action: string) => {
    switch (action) {
      case '新增话术':
        navigate('/script');
        message.info('跳转到话术管理页面');
        break;
      case '创建智能体':
        navigate('/agent');
        message.info('跳转到智能体管理页面');
        break;
      case '导入知识库':
        navigate('/knowledge');
        message.info('跳转到知识库管理页面');
        break;
      case '查看报表':
        navigate('/reports');
        message.info('跳转到报表页面');
        break;
      default:
        message.loading(`正在执行${action}...`, 1);
        setTimeout(() => {
          message.success(`${action}执行成功！`);
        }, 2000);
    }
  };
  const recentConversations = [
    {
      id: 1,
      user: '张三',
      message: '你好，我想了解一下游戏规则',
      time: '2025-10-23 14:30',
      agent: '游戏介绍Bot',
      status: 'success',
    },
    {
      id: 2,
      user: '李四',
      message: '系统维护什么时候结束？',
      time: '2025-10-23 14:25',
      agent: '客服Bot',
      status: 'processing',
    },
    {
      id: 3,
      user: '王五',
      message: '充值没有到账怎么办？',
      time: '2025-10-23 14:20',
      agent: '客服Bot',
      status: 'success',
    },
  ];

  const systemStats = [
    { title: '总用户数', value: 12580, icon: <UserOutlined />, color: '#1890ff' },
    { title: '今日对话数', value: 3240, icon: <MessageOutlined />, color: '#52c41a' },
    { title: '活跃智能体', value: 14, icon: <RobotOutlined />, color: '#722ed1' },
    { title: '知识库条目', value: 2580, icon: <DatabaseOutlined />, color: '#fa8c16' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'green';
      case 'processing': return 'blue';
      case 'error': return 'red';
      default: return 'default';
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>AI智能机器人后台管理系统</h2>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={handleRefresh}
          loading={loading}
          type="primary"
        >
          刷新
        </Button>
      </div>
      <div className="page-header">
        <h1>AI智能机器人后台管理系统</h1>
        <p>欢迎使用AI智能机器人后台管理系统，这里是您的控制中心</p>
      </div>

      {/* 核心指标卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {systemStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <div className="stats-card">
                <div style={{ fontSize: 32, color: stat.color, marginBottom: 8 }}>
                  {stat.icon}
                </div>
                <div className="stats-number" style={{ color: stat.color }}>
                  {stat.value.toLocaleString()}
                </div>
                <div className="stats-label">{stat.title}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* 系统状态 */}
        <Col xs={24} lg={12}>
          <Card title="系统状态" extra={<Button type="link" onClick={() => handleViewDetail('系统状态')}>查看详情</Button>}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>CPU使用率</span>
                <span>45%</span>
              </div>
              <Progress percent={45} status="active" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>内存使用率</span>
                <span>68%</span>
              </div>
              <Progress percent={68} status="active" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>磁盘使用率</span>
                <span>32%</span>
              </div>
              <Progress percent={32} />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>网络延迟</span>
                <span>12ms</span>
              </div>
              <Progress percent={20} status="success" />
            </div>
          </Card>
        </Col>

        {/* 智能体状态 */}
        <Col xs={24} lg={12}>
          <Card title="智能体状态" extra={<Button type="link" onClick={() => handleViewDetail('智能体状态')}>管理智能体</Button>}>
            <List
              size="small"
              dataSource={[
                { name: '气氛活跃Bot', status: 'online', usage: 85 },
                { name: '产品介绍Bot', status: 'online', usage: 72 },
                { name: '客服Bot', status: 'online', usage: 91 },
                { name: '游戏介绍Bot', status: 'online', usage: 68 },
                { name: '情感交流Bot', status: 'online', usage: 45 },
                { name: '搞笑Bot', status: 'offline', usage: 0 },
              ]}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={<RobotOutlined />} size="small" />}
                    title={item.name}
                    description={
                      <div>
                        <Tag color={item.status === 'online' ? 'green' : 'red'}>
                          {item.status === 'online' ? '在线' : '离线'}
                        </Tag>
                        <span style={{ marginLeft: 8 }}>
                          使用率: {item.usage}%
                        </span>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* 最近对话 */}
        <Col xs={24} lg={12}>
          <Card title="最近对话" extra={<Button type="link" onClick={() => handleViewDetail('最近对话')}>查看全部</Button>}>
            <List
              size="small"
              dataSource={recentConversations}
              renderItem={(item) => (
                <List.Item 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleViewConversation(item)}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{item.user}</span>
                        <span style={{ fontSize: 12, color: '#999' }}>{item.time}</span>
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>{item.message}</div>
                        <div>
                          <Tag color={getStatusColor(item.status)}>
                            {item.agent}
                          </Tag>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* 快速操作 */}
        <Col xs={24} lg={12}>
          <Card title="快速操作">
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Button type="primary" block icon={<MessageOutlined />} onClick={() => handleSystemAction('新增话术')}>
                  新增话术
                </Button>
              </Col>
              <Col span={12}>
                <Button block icon={<RobotOutlined />} onClick={() => handleSystemAction('创建智能体')}>
                  创建智能体
                </Button>
              </Col>
              <Col span={12}>
                <Button block icon={<DatabaseOutlined />} onClick={() => handleSystemAction('导入知识库')}>
                  导入知识库
                </Button>
              </Col>
              <Col span={12}>
                <Button block icon={<RiseOutlined />} onClick={() => handleSystemAction('查看报表')}>
                  查看报表
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* 今日数据概览 */}
      <Card title="今日数据概览" style={{ marginTop: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="新增用户"
              value={statsData.totalUsers - 12000}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="对话成功率"
              value={94.5}
              suffix="%"
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="平均响应时间"
              value={1.2}
              suffix="秒"
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
        </Row>
      </Card>

      {/* 详情模态框 */}
      <Modal
        title={`${selectedDetail}详情`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        <div>
          {selectedDetail === '系统状态' && (
            <div>
              <h4>系统监控详情</h4>
              <p>CPU使用率: 45% (正常)</p>
              <p>内存使用率: 68% (正常)</p>
              <p>磁盘使用率: 32% (正常)</p>
              <p>网络延迟: 12ms (良好)</p>
              <p>系统运行时间: 15天8小时</p>
              <p>最后重启时间: 2024-01-08 10:30:00</p>
            </div>
          )}
          {selectedDetail === '智能体状态' && (
            <div>
              <h4>智能体管理</h4>
              <p>总智能体数量: 8个</p>
              <p>在线智能体: 8个</p>
              <p>离线智能体: 0个</p>
              <p>今日调用次数: 15,420次</p>
              <p>平均响应时间: 1.2秒</p>
              <p>成功率: 98.5%</p>
            </div>
          )}
          {selectedDetail === '最近对话' && (
            <div>
              <h4>对话记录详情</h4>
              <p>今日对话总数: 3,240条</p>
              <p>成功对话: 3,180条</p>
              <p>失败对话: 60条</p>
              <p>平均对话时长: 2.5分钟</p>
              <p>用户满意度: 4.2/5.0</p>
              <p>热门话题: 游戏规则、充值问题、技术故障</p>
            </div>
          )}
        </div>
      </Modal>

      {/* 对话详情模态框 */}
      <Modal
        title="对话详情"
        open={conversationModalVisible}
        onCancel={() => setConversationModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setConversationModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        {selectedConversation && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <h4>基本信息</h4>
              <p><strong>用户:</strong> {selectedConversation.user}</p>
              <p><strong>智能体:</strong> {selectedConversation.agent}</p>
              <p><strong>时间:</strong> {selectedConversation.time}</p>
              <p><strong>状态:</strong> <Tag color={getStatusColor(selectedConversation.status)}>{selectedConversation.status}</Tag></p>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <h4>对话内容</h4>
              <div style={{ 
                background: '#f5f5f5', 
                padding: 16, 
                borderRadius: 8,
                border: '1px solid #d9d9d9'
              }}>
                <p><strong>用户消息:</strong></p>
                <p style={{ marginLeft: 16, marginBottom: 12 }}>{selectedConversation.message}</p>
                
                <p><strong>AI回复:</strong></p>
                <p style={{ marginLeft: 16 }}>
                  {selectedConversation.response || '欢迎使用AI智能机器人！我是您的专属助手，可以为您提供游戏规则介绍、产品咨询、情感交流等服务。请问有什么可以帮助您的吗？'}
                </p>
              </div>
            </div>

            <div>
              <h4>对话分析</h4>
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: 16, background: '#f0f9ff', borderRadius: 8 }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>2.3s</div>
                    <div style={{ color: '#666' }}>响应时间</div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: 16, background: '#f6ffed', borderRadius: 8 }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>95%</div>
                    <div style={{ color: '#666' }}>满意度</div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: 16, background: '#fff7e6', borderRadius: 8 }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fa8c16' }}>3</div>
                    <div style={{ color: '#666' }}>交互轮次</div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
